/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import {
  TCalendarSummary,
  TLeaveSummary,
  TReimbursements,
  TSalary,
  TSalarySummary
} from "../../interfaces/SalaryInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import {
  getSalaries,
  postSalary,
  patchSalary,
  deleteSalary,
  calculateEmployeePayrollSummary
} from "../../api/payroll";

export const salariesQueryKey = "salaries";

type SalariesFilter = {
  employeeIds?: number[];
  serviceProviderIds?: number[];
  payrollYear?: number;
  payrollMonth?: number;
  fromDate?: string;
  untilDate?: string;
};

// use salaries
export const useSalaries = (filter?: SalariesFilter): UseQueryResult<Array<TSalary>, Error> => {
  const dispatch = useDispatch();
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [
      salariesQueryKey,
      resourceCentre?.id,
      filter?.employeeIds ?? null,
      filter?.serviceProviderIds ?? null,
      filter?.payrollYear ?? null,
      filter?.payrollMonth ?? null,
      filter?.fromDate ?? null,
      filter?.untilDate ?? null
    ],
    queryFn: async () => {
      const response = await getSalaries({
        ...filter,
        resourceCentreId: Number(resourceCentre?.id)
      });
      if (!response.success) {
        throw new Error(response.message || "Error while fetching salary data");
      }
      return response.data || [];
    },
    enabled: !!resourceCentre?.id,
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: error?.message || "Error while fetching salary data",
          autoTimeout: true
        })
      );
    }
  });
};

// use post salary
export const usePostSalary = (): UseMutationResult<
  ApiResponse<TSalary>,
  Error,
  Partial<TSalary>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<TSalary>) => postSalary(data as any),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [salariesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Salary created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create salary",
          variant: "error"
        })
      );
    }
  });
};

// use patch salary
export const usePatchSalary = (): UseMutationResult<
  ApiResponse<TSalary>,
  Error,
  { id: number | string; data: Partial<TSalary> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => patchSalary({ id, data: data as any }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [salariesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Salary updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update salary",
          variant: "error"
        })
      );
    }
  });
};

// use delete salary
export const useDeleteSalary = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteSalary(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [salariesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Salary deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete salary",
          variant: "error"
        })
      );
    }
  });
};

// use calculate employee payroll summary
export const useCalculateEmployeePayrollSummary = (): UseMutationResult<
  ApiResponse<{
    salarySummary: TSalarySummary;
    leaveSummary: TLeaveSummary;
    calendarSummary: TCalendarSummary;
    reimbursements: TReimbursements;
  }>,
  Error,
  Partial<TSalary>,
  unknown
> => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (data: Partial<TSalary>) => calculateEmployeePayrollSummary(data),
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to calculate CTC",
          variant: "error"
        })
      );
    }
  });
};
